import React, { useEffect, useState } from "react"
import { Box, Typography, Button } from "@mui/material"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import { useTranslation } from "react-i18next"

const CHANNEL_NAME = "caisse-single-tab"

const SingleTabGuard: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [blocked, setBlocked] = useState(false)
    const { t } = useTranslation()

    useEffect(() => {
        const channel = new BroadcastChannel(CHANNEL_NAME)

        // Ask if another tab is already active
        channel.postMessage({ type: "NEW_TAB" })

        // Timeout: if no response within 300ms, we are the only tab
        const timeout = setTimeout(() => {
            // We are the active tab — listen for future newcomers
            channel.onmessage = (event) => {
                if (event.data?.type === "NEW_TAB") {
                    channel.postMessage({ type: "TAB_EXISTS" })
                }
            }
        }, 300)

        // If another tab responds before the timeout, we are blocked
        channel.onmessage = (event) => {
            if (event.data?.type === "TAB_EXISTS") {
                clearTimeout(timeout)
                setBlocked(true)
                channel.close()
            }
        }

        return () => {
            clearTimeout(timeout)
            channel.close()
        }
    }, [])

    if (blocked) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100vh",
                    bgcolor: "#f5f5f5",
                    textAlign: "center",
                    px: 3,
                }}
            >
                <WarningAmberIcon
                    sx={{ fontSize: 64, color: "#f57c00", mb: 2 }}
                />
                <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                    {t("singleTabGuard.title")}
                </Typography>
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ maxWidth: 440, mb: 3 }}
                >
                    {t("singleTabGuard.description")}
                </Typography>
                <Button
                    variant="outlined"
                    onClick={() => window.close()}
                    sx={{ textTransform: "none" }}
                >
                    {t("singleTabGuard.closeButton")}
                </Button>
            </Box>
        )
    }

    return <>{children}</>
}

export default SingleTabGuard
